import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import './Sorteio.css'

/* ═══════════════════════════════════════════════════════════════════ */
/* EVENTO DE LANCAMENTO DIANA — Sorteio com countdown + streaming     */
/* ═══════════════════════════════════════════════════════════════════ */

const ShieldCheckIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = '#00B5A3',
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
)

interface Participant { name: string; email: string }
type ChatPhase = 'idle' | 'sent' | 'analyzing' | 'revealed'

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.testfy.proapp&hl=pt_BR'
const APP_STORE_URL = 'https://apps.apple.com/br/app/testfy-pro/id6759346308'
const TESTFY_API_URL =
  process.env.REACT_APP_API_URL || 'https://api.testfy.com.br'
const SORTEIO_ACTIVE_URL = `${TESTFY_API_URL.replace(/\/$/, '')}/public/diana/sorteios/active`

/* ═══ HOOKS ═══ */

function useStreamText(fullText: string, speed: number = 30, startDelay: number = 0) {
  const [displayed, setDisplayed] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    setDisplayed('')
    setDone(false)
    let i = 0
    let cancelled = false
    const timeouts: ReturnType<typeof setTimeout>[] = []

    const startTimeout = setTimeout(() => {
      const tick = () => {
        if (cancelled) return
        if (i < fullText.length) {
          i++
          setDisplayed(fullText.slice(0, i))
          timeouts.push(setTimeout(tick, speed))
        } else {
          setDone(true)
        }
      }
      tick()
    }, startDelay)
    timeouts.push(startTimeout)

    return () => {
      cancelled = true
      timeouts.forEach(clearTimeout)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { displayed, done }
}

function useCountdown(targetTimestamp: number | null) {
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    if (targetTimestamp === null) {
      setExpired(true)
      setTimeLeft(0)
      return
    }
    setExpired(false)
    const tick = () => {
      const diff = targetTimestamp - Date.now()
      if (diff <= 0) { setTimeLeft(0); setExpired(true); return }
      setTimeLeft(diff)
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [targetTimestamp])

  const hours = timeLeft !== null ? Math.floor(timeLeft / 3600000) : 0
  const minutes = timeLeft !== null ? Math.floor((timeLeft % 3600000) / 60000) : 0
  const seconds = timeLeft !== null ? Math.floor((timeLeft % 60000) / 1000) : 0

  return { hours, minutes, seconds, expired }
}

/* ═══ HELPERS ═══ */

function highlightHA108(text: string) {
  const keyword = 'Hipersensibilidade Alimentar 108'
  const idx = text.indexOf(keyword)
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <strong className="highlight-ha108">{keyword}</strong>
      {text.slice(idx + keyword.length)}
    </>
  )
}

/* ═══ STORE BADGES ═══ */

const AppStoreBadge: React.FC = () => (
  <svg width="120" height="40" viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg">
    <rect width="120" height="40" rx="6" fill="#0F2044"/>
    <g fill="#fff">
      <path d="M24.77 20.3a4.95 4.95 0 0 1 2.36-4.15 5.07 5.07 0 0 0-3.99-2.16c-1.68-.18-3.31 1.01-4.17 1.01-.87 0-2.19-.99-3.62-.96a5.32 5.32 0 0 0-4.48 2.73c-1.93 3.34-.49 8.27 1.36 10.97.93 1.33 2.01 2.82 3.44 2.76 1.39-.06 1.91-.89 3.59-.89 1.66 0 2.14.89 3.59.86 1.49-.02 2.43-1.33 3.33-2.67a10.96 10.96 0 0 0 1.52-3.11 4.78 4.78 0 0 1-2.93-4.39z"/>
      <path d="M22.04 12.21a4.87 4.87 0 0 0 1.12-3.49 4.96 4.96 0 0 0-3.21 1.66 4.64 4.64 0 0 0-1.15 3.36 4.1 4.1 0 0 0 3.24-1.53z"/>
    </g>
    <g fill="#fff">
      <text x="38" y="16" fontSize="8" fontFamily="system-ui" fontWeight="400" letterSpacing="0.03em">Download on the</text>
      <text x="38" y="28" fontSize="13" fontFamily="system-ui" fontWeight="600">App Store</text>
    </g>
  </svg>
)

const GooglePlayBadge: React.FC = () => (
  <svg width="135" height="40" viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg">
    <rect width="135" height="40" rx="6" fill="#0F2044"/>
    <g transform="translate(10, 5)">
      <path d="M6.35 3.17L17.24 14.7 6.35 26.22a2.55 2.55 0 0 1-.35-1.3V4.47c0-.46.12-.9.35-1.3z" fill="#4285F4"/>
      <path d="M21.12 18.57l-3.88-3.87 3.88-3.88 4.37 2.48a1.53 1.53 0 0 1 0 2.78l-4.37 2.49z" fill="#FBBC04"/>
      <path d="M6.35 3.17a2.5 2.5 0 0 1 1.32-.4c.37 0 .73.1 1.06.3L21.12 10.82 17.24 14.7 6.35 3.17z" fill="#34A853"/>
      <path d="M17.24 14.7l3.88 3.87L8.73 26.32a2.38 2.38 0 0 1-1.06.3 2.5 2.5 0 0 1-1.32-.4L17.24 14.7z" fill="#EA4335"/>
    </g>
    <g fill="#fff">
      <text x="44" y="16" fontSize="7.5" fontFamily="system-ui" fontWeight="400" letterSpacing="0.05em">GET IT ON</text>
      <text x="44" y="28" fontSize="12.5" fontFamily="system-ui" fontWeight="600">Google Play</text>
    </g>
  </svg>
)

/* ═══ AUTH ═══ */

const AUTH_KEY = 'diana_event_auth'
const AUTH_TTL = 48 * 60 * 60 * 1000

function checkAuth(): boolean {
  const stored = localStorage.getItem(AUTH_KEY)
  if (!stored) return false
  try { const { ts } = JSON.parse(stored); return Date.now() - ts < AUTH_TTL }
  catch { return false }
}

function setAuth() {
  localStorage.setItem(AUTH_KEY, JSON.stringify({ ts: Date.now() }))
}

function LoginGate({ onAuth }: { onAuth: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (email.trim().toLowerCase() === 'diana@testfy.com.br' && password === 'Testfy@4893') {
      setAuth(); onAuth()
    } else {
      setError(true); setTimeout(() => setError(false), 2000)
    }
  }

  return (
    <div className="login-gate">
      <div className="login-card">
        <img src="/assets/images/diana.png" alt="Diana" className="login-diana" />
        <h2 className="login-title">Evento Diana</h2>
        <p className="login-sub">Acesso restrito à equipe</p>
        <form className="login-form" onSubmit={handleLogin}>
          <input type="email" className={`login-input${error ? ' login-error' : ''}`} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          <input type="password" className={`login-input${error ? ' login-error' : ''}`} placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <span className="login-error-text">Credenciais inválidas</span>}
          <button type="submit" className="login-btn">Entrar</button>
        </form>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════ */
/* SORTEIO SCREEN — Componente separado para remount  */
/* ═══════════════════════════════════════════════════ */

function SorteioScreen({
  participants,
  onLogoClick,
  productName,
  productDescription,
  sorteioId,
}: {
  participants: Participant[]
  onLogoClick: () => void
  productName: string
  productDescription: string
  sorteioId: string | null
}) {
  const [phase, setPhase] = useState<ChatPhase>('idle')
  const [currentName, setCurrentName] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [winner, setWinner] = useState<Participant | null>(null)
  const [showConfetti, setShowConfetti] = useState(false)
  const [introReady, setIntroReady] = useState(false)

  const rollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* ─── Streaming intro ─── */
  const greeting = useStreamText('Oi, eu sou a Diana!', 40, 600)
  const desc = useStreamText(
    productDescription,
    18, 1600
  )
  const detail = useStreamText('Coleta de sangue capilar — rápida e prática.', 22, 7200)
  const cta = useStreamText('Vamos descobrir quem vai ganhar?', 35, 8800)

  useEffect(() => { if (cta.done && !introReady) setIntroReady(true) }, [cta.done, introReady])
  useEffect(() => { return () => { if (rollRef.current) clearTimeout(rollRef.current) } }, [])

  const handleSend = useCallback(() => {
    if (participants.length === 0 || phase === 'analyzing' || phase === 'sent') return
    setPhase('sent')
    setWinner(null); setShowConfetti(false); setCurrentName('')

    setTimeout(() => {
      setPhase('analyzing')
      const winnerEntry = participants[Math.floor(Math.random() * participants.length)]
      const totalDuration = 10000
      const startTime = Date.now()
      const easeInQuart = (t: number) => t * t * t * t
      const statusMessages = [
        'Analisando participantes inscritos...',
        'Consultando base científica...',
        'Cruzando dados com protocolos Greenbook...',
        'Verificando elegibilidade...',
        'Analisando perfil dos participantes...',
        'Processando dados clínicos...',
        'Quase lá... finalizando análise...',
        'Definindo o ganhador...',
      ]

      const roll = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / totalDuration, 1)
        setStatusMessage(statusMessages[Math.min(Math.floor(progress * statusMessages.length), statusMessages.length - 1)])

        if (progress >= 1) {
          setCurrentName(winnerEntry.name)
          setPhase('revealed'); setWinner(winnerEntry); setShowConfetti(true)
          setTimeout(() => setShowConfetti(false), 10000)
          // Persiste o ganhador no backend (silent fail — sorteio segue
          // visualmente mesmo se a chamada quebrar; backend é idempotente).
          if (sorteioId) {
            fetch(`${TESTFY_API_URL.replace(/\/$/, '')}/public/diana/sorteios/${sorteioId}/winner`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: winnerEntry.name, email: winnerEntry.email }),
            }).catch((err) => console.error('registerWinner failed:', err))
          }
          return
        }
        setCurrentName(participants[Math.floor(Math.random() * participants.length)].name)
        rollRef.current = setTimeout(roll, 40 + easeInQuart(progress) * 1200)
      }
      roll()
    }, 800)
  }, [participants, phase])

  /* ─── Confetti ─── */
  const confettiColors = useMemo(() => ['#00B5A3', '#047857', '#FFD700', '#0F2044', '#4ECDC4', '#FF6B6B'], [])
  const confettiPieces = useMemo(() => {
    if (!showConfetti) return []
    return Array.from({ length: 100 }).map((_, i) => ({
      id: i, left: `${Math.random() * 100}%`, delay: `${Math.random() * 2.5}s`,
      duration: `${2.5 + Math.random() * 3}s`, color: confettiColors[i % confettiColors.length],
      width: `${6 + Math.random() * 8}px`, height: `${6 + Math.random() * 8}px`,
    }))
  }, [showConfetti, confettiColors])

  const userMessage = `Diana, quem é o ganhador do sorteio da análise ${productName.toUpperCase()}?`
  const sendDisabled = participants.length === 0 || phase !== 'idle' || !introReady

  return (
    <div className="event-page">
      {showConfetti && (
        <div className="confetti-container">
          {confettiPieces.map((p) => (
            <div key={p.id} className="confetti-piece" style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration, backgroundColor: p.color, width: p.width, height: p.height }} />
          ))}
        </div>
      )}

      <header className="event-header">
        <div className="event-header-inner">
          <img src="/assets/images/testfy-white.svg" alt="Testfy" className="event-logo event-logo--clickable" onClick={onLogoClick} />
        </div>
      </header>

      <div className="event-title-bar">
        <h1 className="event-title">SORTEIO TESTFY</h1>
        <p className="event-subtitle">{productName}</p>
      </div>

      <main className="event-main">
        <div className="event-stage">
          <div className="diana-figure">
            <img src="/assets/images/diana.png" alt="Diana" className="diana-figure-img" />
            <span className="diana-figure-label">Diana</span>
            <span className="diana-figure-sub">
              Inteligência Artificial da Testfy<br />
              <span className="diana-figure-gb">by GREENBOOK</span>
            </span>
          </div>

          <div className="speech-area">
            <div className={`diana-speech${phase === 'analyzing' ? ' analyzing' : ''}${phase === 'revealed' ? ' revealed' : ''}`}>
              <div className="diana-speech-arrow" />

              {phase === 'idle' && (
                <div className="speech-intro">
                  {greeting.displayed && (
                    <p className="stream-greeting">{greeting.displayed}{!greeting.done && <span className="stream-cursor" />}</p>
                  )}
                  {desc.displayed && (
                    <p className="stream-desc">{highlightHA108(desc.displayed)}{!desc.done && <span className="stream-cursor" />}</p>
                  )}
                  {detail.displayed && (
                    <p className="stream-detail">{detail.displayed}{!detail.done && <span className="stream-cursor" />}</p>
                  )}
                  {cta.displayed && (
                    <p className="stream-cta">{cta.displayed}{!cta.done && <span className="stream-cursor" />}</p>
                  )}
                  {introReady && participants.length > 0 && (
                    <p className="stream-participants-count">{participants.length} participantes inscritos</p>
                  )}
                </div>
              )}

              {phase === 'sent' && (
                <div className="speech-loading"><div className="typing-dots"><span /><span /><span /></div></div>
              )}

              {phase === 'analyzing' && (
                <div className="speech-analyzing">
                  <div className="typing-dots typing-dots--large"><span /><span /><span /></div>
                  <div className="speech-thinking-stream"><p className="thinking-line">{statusMessage}</p></div>
                </div>
              )}

              {phase === 'revealed' && winner && (
                <div className="speech-result">
                  <div className="speech-result-congrats">Parabéns! O ganhador(a) do sorteio é:</div>
                  <div className="speech-result-winner">
                    <div className="speech-result-name">{winner.name}</div>
                    {winner.email && <div className="speech-result-email">{winner.email}</div>}
                  </div>
                  <div className="speech-result-divider" />
                  <div className="speech-result-about">
                    <div className="speech-result-about-title">Sobre o Teste de {productName}</div>
                    <p>{productDescription}</p>
                  </div>
                  <span className="speech-result-tag">Baseado em protocolos Greenbook</span>
                </div>
              )}
            </div>

            {(phase === 'sent' || phase === 'analyzing' || phase === 'revealed') && (
              <div className="user-question"><span className="user-question-text">{userMessage}</span></div>
            )}
          </div>
        </div>

        {phase !== 'revealed' && introReady && (
          <div className="diana-controls diana-controls--enter">
            <div className={`diana-input-pill${phase !== 'idle' ? ' disabled' : ''}${sendDisabled ? ' waiting' : ''}`}>
              <span className="diana-input-text">
                {phase === 'idle' ? (
                  <>Diana, quem é o ganhador do sorteio da análise <strong className="highlight-ha108">{productName.toUpperCase()}</strong>?</>
                ) : 'Diana está analisando...'}
              </span>
              <button className="diana-send-btn" onClick={handleSend} disabled={sendDisabled}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {phase === 'revealed' && winner && (
          <>
            <div className="reveal-download-cta">
              <p className="reveal-download-text">Baixe o app <strong>Testfy Pro</strong></p>
              <div className="reveal-download-qr">
                <div className="reveal-qr-group">
                  <div className="event-qr-wrap"><QRCodeSVG value={APP_STORE_URL} size={64} bgColor="#FFFFFF" fgColor="#0F2044" level="M" /></div>
                  <AppStoreBadge />
                </div>
                <div className="reveal-qr-group">
                  <div className="event-qr-wrap"><QRCodeSVG value={PLAY_STORE_URL} size={64} bgColor="#FFFFFF" fgColor="#0F2044" level="M" /></div>
                  <GooglePlayBadge />
                </div>
              </div>
            </div>

            <div className="participants-list">
              <table className="participants-table">
                <thead><tr><th className="th-num">#</th><th>Nome</th><th></th></tr></thead>
                <tbody>
                  <tr className="participant-row participant-row--winner">
                    <td className="participant-num">1</td>
                    <td className="participant-name">{winner.name}</td>
                    <td className="participant-badge-cell"><span className="winner-pill">Ganhador</span></td>
                  </tr>
                  {participants
                    .filter((p) => p.name !== winner.name || p.email !== winner.email)
                    .map((p, i) => (
                      <tr key={i} className="participant-row">
                        <td className="participant-num">{i + 2}</td>
                        <td className="participant-name">{p.name}</td>
                        <td className="participant-badge-cell" />
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      <footer className="event-footer-bar">
        <span className="footer-copy">Tenha a Diana no seu bolso — sua assistente de inteligência clínica, disponível 24h</span>
        <div className="event-qr-row-inline">
          <div className="event-qr-group">
            <div className="event-qr-wrap"><QRCodeSVG value={APP_STORE_URL} size={48} bgColor="#FFFFFF" fgColor="#0F2044" level="M" /></div>
            <AppStoreBadge />
          </div>
          <div className="event-qr-group">
            <div className="event-qr-wrap"><QRCodeSVG value={PLAY_STORE_URL} size={48} bgColor="#FFFFFF" fgColor="#0F2044" level="M" /></div>
            <GooglePlayBadge />
          </div>
        </div>
      </footer>
    </div>
  )
}

/* ═══════════════════════════════════════════════════ */
/* APP — Orchestrator                                  */
/* ═══════════════════════════════════════════════════ */

const FALLBACK_PRODUCT_NAME = 'Hipersensibilidade Alimentar 108'
const FALLBACK_PRODUCT_DESCRIPTION =
  'A análise de Hipersensibilidade Alimentar 108 avalia a reação dos anticorpos IgG a 108 alimentos diferentes, identificando possíveis responsáveis por disfunções gastrointestinais, doenças inflamatórias da pele, enxaqueca e síndrome da fadiga crônica.'

function SorteioApp() {
  const [authed, setAuthed] = useState(checkAuth)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [manualOverride, setManualOverride] = useState(false)
  const [sorteioKey, setSorteioKey] = useState(0)
  const [startsAt, setStartsAt] = useState<number | null>(null)
  const [productName, setProductName] = useState<string>(FALLBACK_PRODUCT_NAME)
  const [productDescription, setProductDescription] = useState<string>(
    FALLBACK_PRODUCT_DESCRIPTION
  )
  const [sorteioId, setSorteioId] = useState<string | null>(null)

  const countdown = useCountdown(startsAt)
  const showSorteio = countdown.expired || manualOverride

  const handleLogoClick = useCallback(() => {
    setManualOverride((prev) => {
      if (!prev) setSorteioKey((k) => k + 1) // increment key to remount fresh
      return !prev
    })
  }, [])

  /* ─── Fetch participants from testfy-core-api ─── */
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await fetch(SORTEIO_ACTIVE_URL)
        if (!res.ok) throw new Error('Erro ao carregar')
        const data = await res.json() as {
          id?: string
          participants?: { name: string; email: string }[]
          starts_at?: string | null
          product_name?: string | null
          product_description?: string | null
        }
        if (data.id) setSorteioId(data.id)
        const parsed: Participant[] = (data.participants ?? [])
          .map((p) => ({
            name: String(p.name ?? '').trim(),
            email: String(p.email ?? '').trim(),
          }))
          .filter((p) => p.name)
        if (cancelled) return
        if (parsed.length > 0) setParticipants(parsed)
        if (data.starts_at) {
          const ts = new Date(data.starts_at).getTime()
          if (!Number.isNaN(ts)) setStartsAt(ts)
        }
        if (data.product_name) setProductName(data.product_name)
        if (data.product_description)
          setProductDescription(data.product_description)
      } catch (err) { console.error('Erro ao buscar participantes:', err) }
    }
    load()
    return () => { cancelled = true }
  }, [])

  if (!authed) {
    return <LoginGate onAuth={() => setAuthed(true)} />
  }

  /* ─── Countdown screen ─── */
  if (!showSorteio) {
    const hh = String(countdown.hours).padStart(2, '0')
    const mm = String(countdown.minutes).padStart(2, '0')
    const ss = String(countdown.seconds).padStart(2, '0')

    return (
      <div className="event-page">
        <header className="event-header">
          <div className="event-header-inner">
            <img src="/assets/images/testfy-white.svg" alt="Testfy" className="event-logo event-logo--clickable" onClick={handleLogoClick} />
          </div>
        </header>

        <main className="countdown-main">
          <div className="event-title-bar">
            <h2 className="event-title">SORTEIO TESTFY</h2>
            <p className="event-subtitle">{productName}</p>
          </div>

          <img src="/assets/images/diana.png" alt="Diana" className="countdown-diana" />
          <h1 className="countdown-title">Diana</h1>
          <p className="countdown-sub">Inteligência Artificial da Testfy · <span className="diana-figure-gb">by GREENBOOK</span></p>

          <div className="countdown-timer-block">
            <span className="countdown-label">COMEÇA EM</span>
            <div className="countdown-digits">
              <span className="countdown-num">{hh}</span>
              <span className="countdown-sep">:</span>
              <span className="countdown-num">{mm}</span>
              <span className="countdown-sep">:</span>
              <span className="countdown-num">{ss}</span>
            </div>
          </div>

          <div className="countdown-qr">
            <span className="countdown-qr-text">Baixe o app Testfy Pro</span>
            <div className="event-qr-row-inline">
              <div className="event-qr-group">
                <div className="event-qr-wrap"><QRCodeSVG value={APP_STORE_URL} size={48} bgColor="#FFFFFF" fgColor="#0F2044" level="M" /></div>
                <AppStoreBadge />
              </div>
              <div className="event-qr-group">
                <div className="event-qr-wrap"><QRCodeSVG value={PLAY_STORE_URL} size={48} bgColor="#FFFFFF" fgColor="#0F2044" level="M" /></div>
                <GooglePlayBadge />
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  /* ─── Sorteio screen — key forces fresh remount ─── */
  return (
    <SorteioScreen
      key={sorteioKey}
      participants={participants}
      onLogoClick={handleLogoClick}
      productName={productName}
      productDescription={productDescription}
      sorteioId={sorteioId}
    />
  )
}

export default SorteioApp
